import type { Dict } from "../types";

export const announcements: Dict = {
  en: {

    // Roadmap (owner-facing "Product Roadmap")
    "Roadmap.title": "Product Roadmap",
    "Announcements.close": "Close",
    "Roadmap.subtitle": "What we're building at Flowcut right now",
    "Roadmap.empty": "Nothing to show yet. Check back soon!",
    "Roadmap.offline": "No connection. The list will appear once you are back online.",
    "Roadmap.loadFailed": "Could not load the items.",
    "Roadmap.loadFailedAction": "Try again",
    "Roadmap.status.planned": "Planned",
    "Roadmap.status.in_progress": "In progress",
    "Roadmap.status.shipped": "Shipped",

    // Roadmap (admin management screen)
    "Roadmap.admin.title": "Product Roadmap management",
    "Roadmap.admin.subtitle": "What salon owners see in their dashboard",
    "Roadmap.admin.addItem": "Add item",
    "Roadmap.admin.editItem": "Edit item",
    "Roadmap.admin.empty": "No items yet. Add one to see it appear for salon owners.",
    "Roadmap.admin.fieldTitle": "Title",
    "Roadmap.admin.fieldDescription": "Description",
    "Roadmap.admin.fieldPeriod": "Timeline",
    "Roadmap.admin.fieldPeriodPlaceholder": "e.g. Q3 2026",
    "Roadmap.admin.fieldStatus": "Status",
    "Roadmap.admin.fieldSortOrder": "Display order",
    "Roadmap.admin.sortOrderHint": "Lower numbers appear first.",
    "Roadmap.admin.fieldPublished": "Published",
    "Roadmap.admin.publishedHint": "Until it's published, only admins can see it.",
    "Roadmap.admin.draft": "Draft",
    "Roadmap.admin.frenchOnly": "French only",
    "Roadmap.admin.titleRequired": "A title is required to save.",
    "Roadmap.admin.deleteConfirmTitle": "Delete this item?",
    "Roadmap.admin.deleteConfirmBody": "This action cannot be undone.",
    "Roadmap.admin.deleteConfirmTitleNamed": "Delete the item {name}?",
    "Roadmap.admin.deleteError": "Could not delete the item",
    "Roadmap.admin.deleteRefused": "The deletion was refused. The item is still there.",
    "Roadmap.admin.created": "Item added",
    "Roadmap.admin.updated": "Item updated",
    "Roadmap.admin.deleted": "Item deleted",
    "Roadmap.admin.saveError": "Could not save the item",

    // Announcements (admin composer)
    "Announcements.admin.title": "Announcements",
    "Announcements.admin.subtitle": "Send a message inside the app to owners and employees",
    "Announcements.admin.new": "New announcement",
    "Announcements.admin.editItem": "Edit announcement",
    "Announcements.admin.titleFr": "Title (FR)",
    "Announcements.admin.titleEn": "Title (EN)",
    "Announcements.admin.bodyFr": "Message (FR)",
    "Announcements.admin.bodyEn": "Message (EN)",
    "Announcements.admin.linkLabel": "Link label (FR)",
    "Announcements.admin.linkLabelEn": "Link label (EN)",
    "Announcements.admin.linkUrl": "Destination",
    "Announcements.admin.audience": "Audience",
    "Announcements.admin.audienceOwners": "Owners",
    "Announcements.admin.audienceEmployees": "Employees",
    "Announcements.admin.audienceBoth": "Both",
    "Announcements.admin.targeting": "Targeting",
    "Announcements.admin.targetingAll": "All salons",
    "Announcements.admin.targetingTier": "By plan",
    "Announcements.admin.maxViews": "Number of views",
    "Announcements.admin.publishNow": "Publish now",
    "Announcements.admin.saveDraft": "Save draft",
    "Announcements.admin.publish": "Publish",
    "Announcements.admin.draft": "Draft",
    "Announcements.admin.published": "Published",
    "Announcements.admin.empty": "No announcement yet.",
    "Announcements.admin.created": "Announcement created",
    "Announcements.admin.updated": "Announcement updated",
    "Announcements.admin.deleted": "Announcement deleted",
    "Announcements.admin.saveFailed": "Could not save the announcement.",
    "Announcements.admin.deleteFailed": "Could not delete the announcement.",
    "Announcements.admin.deleteConfirmTitle": "Delete this announcement?",
    "Announcements.admin.deleteConfirmBody": "This action cannot be undone.",
    "Announcements.admin.reachNone": "No views yet.",
    "Announcements.admin.reachViewersOne": "Seen by {viewers} person",
    "Announcements.admin.reachViewersOther": "Seen by {viewers} people",
    "Announcements.admin.reachExhaustedOne": "{exhausted} has used up their views.",
    "Announcements.admin.reachExhaustedOther": "{exhausted} have used up their views.",
  },
  fr: {

    // Roadmap (vue propriétaire « Nouveautés »)
    "Roadmap.title": "Nouveautés",
    "Announcements.close": "Fermer",
    "Roadmap.subtitle": "Ce qu'on développe en ce moment chez Flowcut",
    "Roadmap.empty": "Rien à afficher pour l'instant. Revenez bientôt !",
    "Roadmap.offline": "Pas de connexion. La liste s'affichera au retour du réseau.",
    "Roadmap.loadFailed": "Impossible de charger les nouveautés.",
    "Roadmap.loadFailedAction": "Réessayer",
    "Roadmap.status.planned": "Prévu",
    "Roadmap.status.in_progress": "En cours",
    "Roadmap.status.shipped": "Livré",

    // Roadmap (écran de gestion admin)
    "Roadmap.admin.title": "Gestion des nouveautés",
    "Roadmap.admin.subtitle": "Ce que voient les propriétaires dans leur tableau de bord",
    "Roadmap.admin.addItem": "Ajouter une nouveauté",
    "Roadmap.admin.editItem": "Modifier la nouveauté",
    "Roadmap.admin.empty": "Aucune nouveauté. Ajoutez-en une pour la voir apparaître chez les propriétaires.",
    "Roadmap.admin.fieldTitle": "Titre",
    "Roadmap.admin.fieldDescription": "Description",
    "Roadmap.admin.fieldPeriod": "Échéance",
    "Roadmap.admin.fieldPeriodPlaceholder": "ex. T3 2026",
    "Roadmap.admin.fieldStatus": "Statut",
    "Roadmap.admin.fieldSortOrder": "Ordre d'affichage",
    "Roadmap.admin.sortOrderHint": "Les plus petits nombres apparaissent en premier.",
    "Roadmap.admin.fieldPublished": "Publié",
    "Roadmap.admin.publishedHint": "Tant que ce n'est pas publié, seuls les admins le voient.",
    "Roadmap.admin.draft": "Brouillon",
    "Roadmap.admin.frenchOnly": "Français seulement",
    "Roadmap.admin.titleRequired": "Un titre est nécessaire pour enregistrer.",
    "Roadmap.admin.deleteConfirmTitle": "Supprimer cette nouveauté ?",
    "Roadmap.admin.deleteConfirmBody": "Cette action est irréversible.",
    "Roadmap.admin.deleteConfirmTitleNamed": "Êtes-vous sûr de vouloir supprimer la nouveauté {name} ?",
    "Roadmap.admin.deleteError": "Impossible de supprimer la nouveauté",
    "Roadmap.admin.deleteRefused": "La suppression a été refusée. La nouveauté est toujours là.",
    "Roadmap.admin.created": "Nouveauté ajoutée",
    "Roadmap.admin.updated": "Nouveauté mise à jour",
    "Roadmap.admin.deleted": "Nouveauté supprimée",
    "Roadmap.admin.saveError": "Impossible d'enregistrer la nouveauté",

    // Annonces (composeur admin)
    "Announcements.admin.title": "Annonces",
    "Announcements.admin.subtitle": "Envoyer un message dans l'app aux propriétaires et aux employés",
    "Announcements.admin.new": "Nouvelle annonce",
    "Announcements.admin.editItem": "Modifier l'annonce",
    "Announcements.admin.titleFr": "Titre (FR)",
    "Announcements.admin.titleEn": "Titre (EN)",
    "Announcements.admin.bodyFr": "Message (FR)",
    "Announcements.admin.bodyEn": "Message (EN)",
    "Announcements.admin.linkLabel": "Libellé du lien (FR)",
    "Announcements.admin.linkLabelEn": "Libellé du lien (EN)",
    "Announcements.admin.linkUrl": "Destination",
    "Announcements.admin.audience": "Audience",
    "Announcements.admin.audienceOwners": "Propriétaires",
    "Announcements.admin.audienceEmployees": "Employés",
    "Announcements.admin.audienceBoth": "Les deux",
    "Announcements.admin.targeting": "Ciblage",
    "Announcements.admin.targetingAll": "Tous les salons",
    "Announcements.admin.targetingTier": "Par forfait",
    "Announcements.admin.maxViews": "Nombre de vues",
    "Announcements.admin.publishNow": "Publier maintenant",
    "Announcements.admin.saveDraft": "Enregistrer le brouillon",
    "Announcements.admin.publish": "Publier",
    "Announcements.admin.draft": "Brouillon",
    "Announcements.admin.published": "Publiée",
    "Announcements.admin.empty": "Aucune annonce pour le moment.",
    "Announcements.admin.created": "Annonce créée",
    "Announcements.admin.updated": "Annonce modifiée",
    "Announcements.admin.deleted": "Annonce supprimée",
    "Announcements.admin.saveFailed": "L'annonce n'a pas pu être enregistrée.",
    "Announcements.admin.deleteFailed": "L'annonce n'a pas pu être supprimée.",
    "Announcements.admin.deleteConfirmTitle": "Supprimer cette annonce ?",
    "Announcements.admin.deleteConfirmBody": "Cette action est irréversible.",
    "Announcements.admin.reachNone": "Aucune vue pour le moment.",
    "Announcements.admin.reachViewersOne": "Vue par {viewers} personne",
    "Announcements.admin.reachViewersOther": "Vue par {viewers} personnes",
    "Announcements.admin.reachExhaustedOne": "{exhausted} a épuisé ses vues.",
    "Announcements.admin.reachExhaustedOther": "{exhausted} ont épuisé leurs vues.",
  },
};
